import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "pd_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isiOS() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const safari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return (iOSDevice || iPadOS) && safari;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS Safari has no beforeinstallprompt — show manual "Add to Home Screen" hint.
    if (isiOS()) {
      setIosHint(true);
      setShow(true);
    }

    const onInstalled = () => {
      setShow(false);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (_) {}
    setDeferred(null);
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed left-1/2 -translate-x-1/2 bottom-4 z-[100] w-[calc(100%-2rem)] max-w-[398px]"
    >
      <div className="bg-[#1A1A1A] border border-[#333333] rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3">
        <img src="/icon-192.png" alt="PocketDocta" className="w-11 h-11 rounded-xl shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-head font-bold text-[#F8FAFC] text-sm">Install PocketDocta</p>
          {iosHint ? (
            <p className="text-xs text-[#A1A1AA] leading-snug mt-0.5">
              Tap <Share size={12} className="inline -mt-0.5 text-[#EF9F27]" /> Share, then{" "}
              <span className="text-[#F8FAFC] font-semibold">Add to Home Screen</span>.
            </p>
          ) : (
            <p className="text-xs text-[#A1A1AA] leading-snug mt-0.5">
              Add it to your home screen for quick, app-like access.
            </p>
          )}
        </div>
        {!iosHint && (
          <button
            data-testid="install-app-btn"
            onClick={install}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#EF9F27] text-[#111111] font-bold text-sm px-3.5 py-2 min-h-[40px]"
          >
            <Download size={15} /> Install
          </button>
        )}
        <button
          data-testid="install-dismiss-btn"
          aria-label="Dismiss install prompt"
          onClick={dismiss}
          className="shrink-0 min-h-[40px] min-w-[36px] flex items-center justify-center text-[#666666] hover:text-[#A1A1AA]"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
